import { useLazyQuery } from "@apollo/client";
import { Grid, useMediaQuery } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import Table from "components/Table";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import Filter from "domains/CashManagement/components/dialogs/Filter";
import { Header } from "domains/CashManagement/components/Header";
import { RecapCard } from "domains/CashManagement/components/RecapCard";
import { cardActivitionIdentifier, useParcelRowColumns } from "domains/CashManagement/data";
import { CashLayout, useCashStyles } from "domains/CashManagement/style";
import { cashRecapPageStatuts, defaultFilterValues, FilterForm } from "domains/CashManagement/types";
import { ParcelRecapStatus } from "generated/graphql";
import { GET_CASH_PARCEL_RECAP } from "graphql/cashManagement/queries";
import { useCurrentClient } from "hooks";
import React, { FC } from "react";
import { isSuccess } from "utils/https";


  
  // eslint-disable-next-line arrow-body-style
  export const CahRecap: FC = (props) => {
    const [cashMenu ,setCashMenu] = React.useState([]);
    const [parcels ,setParcels] = React.useState([]);
    const [status , setStatus] = React.useState<string>();
    const [skeleton, setSkeleton] = React.useState(Array.from(new Array(5)));
    const {client} = useCurrentClient();
    const [searchTerm, setSearchTerm] = React.useState("");
    const columns = useParcelRowColumns();
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [countTotal, setCountTotal] = React.useState<number>();
    const [page, setPage] = React.useState<number>(0);
    const [activeCard, setActiveCard] = React.useState<number>(0);
    const classes = useCashStyles();
    const [hasFilters, setHasFilters] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [cashQuery, setCashQuery] = React.useState<FilterForm>(defaultFilterValues);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    const [getCashParcels, {loading}] = useLazyQuery(GET_CASH_PARCEL_RECAP, {
      onCompleted: (data) => {
        if (isSuccess(data)) {
          setParcels(data?.results?.data[0]?.parcels);
          setCountTotal(data?.results?.data[0].count)
        }
      },
      fetchPolicy: "no-cache",
    });

    const [getCashMenu, {loading:isCashLoading}] = useLazyQuery(GET_CASH_PARCEL_RECAP, {
      onCompleted: (data) => {
        if (isSuccess(data)) {
          setCashMenu(data?.results?.data)
        }
      },
      fetchPolicy: "cache-and-network",
    });

    const onClose = () => {
      setOpen(false);
    };

    const onConfirm = () => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      getCashParcels({
        variables: {
          queries: {
            client_id: client?._id,
            receiver_id: cashQuery?.receiver?._id,
            status: !status ? cashRecapPageStatuts : [status],
            from_date:cashQuery?.from,
            to_date:cashQuery?.to
          },
        },
      });

      getCashMenu({
        variables: {
          queries: {
            status:cashRecapPageStatuts,
            client_id: client?._id,
            receiver_id: cashQuery?.receiver?._id,
            from_date:cashQuery?.from,
            to_date:cashQuery?.to,
          },
        },
      })
    };


    const handleClick = (FilterStatus : string) => {
      setStatus(FilterStatus);
      setPage(0)
    }

    const handleChangePage = (event: unknown, newpage: number) => {
      setPage(newpage);
    };

    React.useEffect(()=>{
      getCashParcels({
        variables: {
          queries: {
            client_id: client?._id,
            status: !status  ? cashRecapPageStatuts : [status],
            from_date:cashQuery?.from,
            to_date:cashQuery?.to,
            receiver_id: cashQuery?.receiver?._id,
            page: page + 1,
            limit: rowsPerPage,
          },
        },
      });
      getCashMenu({
        variables: {
          queries: {
            status:cashRecapPageStatuts,
            client_id: client?._id,
            from_date:cashQuery?.from,
            to_date:cashQuery?.to,
            receiver_id: cashQuery?.receiver?._id,
          },
        },
      })
      setActiveCard(cardActivitionIdentifier[status]);
     !status && setActiveCard(0)
    },[client, status, page, rowsPerPage])

    const clearFilter =()=>{
      getCashParcels({
        variables: {
          queries: {
            status:!status ? [ParcelRecapStatus.CollectedByCashier] : [status],
            client_id: client?._id,
            receiver_id: null,
            from_date:null,
            to_date:null,
          },
        },
      });
      getCashMenu({
        variables: {
          queries: {
            status:cashRecapPageStatuts,
            client_id: client?._id,
            receiver_id: null,
            from_date:null,
            to_date:null,
          },
        },
      })
     
      setCashQuery(defaultFilterValues);
    }

      return (
        <CashLayout>
          <Header
            setSearchTerm={setSearchTerm}
            title="Cash recap"
            clearFilter={clearFilter}
            searchTerm={searchTerm}
            buttonClick={setOpen}
            cashQuery={cashQuery}
          />
          <Grid className="card-container">
            {isCashLoading && !cashMenu?.length
              ? skeleton?.map(() => <Skeleton className="skeleton" width="auto" height={200} animation="wave" />)
              : cashMenu?.map(({ count, status, amount, _id }: any, index) => (
                  <RecapCard
                    key={_id}
                    amount={amount}
                    count={count}
                    status={status}
                    clickAction={() => handleClick(status)}
                    activeCard={activeCard}
                    identifier={index}
                    isParcelCount
                  />
                ))}
          </Grid>

          <div
            className={classes.table}
            style={{ display: isSmallScreen && "inherit"}}
          >
            <Table
              tableHeight={isSmallScreen ? "38vh" : "52vh"}
              searchTerm={searchTerm}
              rowsIdentifier="parcel_UID"
              columns={columns}
              data={parcels}
              isLoading={loading}
              rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
              rowsPerPage={rowsPerPage}
              currentPage={page}
              countTotal={countTotal}
              setRowsPerPage={setRowsPerPage}
              onChangePage={handleChangePage}
            />
          </div>

          <Filter
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
            setHasFilters={setHasFilters}
            cashQuery={cashQuery}
            setCashQuery={setCashQuery}
            clientID={client?._id}
            isReceiver
          />
        </CashLayout>
      );
  };